import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { MoreVertical, Edit3, Link, Lock, Unlock, Users, Trash2, QrCode, Ticket, Image, MessageSquare, BarChart } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import type { JamNight } from "@shared/schema";
import QRCode from "qrcode";
import EditEventModal from "@/components/edit-event-modal";
import { EventLogoModal } from "@/components/event-logo-modal";

interface EventActionsDropdownProps {
  jamNight: JamNight;
  canManageEvent: boolean;
  variant?: "icon" | "button";
  className?: string;
  onActionStart?: () => void;
  onActionEnd?: () => void;
}

export function EventActionsDropdown({ 
  jamNight, 
  canManageEvent, 
  variant = "icon",
  className = ""
}: EventActionsDropdownProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [signupLinkModal, setSignupLinkModal] = useState<{ open: boolean; url: string; eventName: string }>({
    open: false,
    url: "",
    eventName: ""
  });
  const [signupLimitModal, setSignupLimitModal] = useState<{ open: boolean; jamNightId: number; currentLimit: number | null; eventName: string }>({
    open: false,
    jamNightId: 0,
    currentLimit: null,
    eventName: ""
  });
  const [signupLimitValue, setSignupLimitValue] = useState<string>("");
  const [qrCodeModal, setQrCodeModal] = useState<{ open: boolean; qrCodeDataUrl: string; eventName: string }>({
    open: false,
    qrCodeDataUrl: "",
    eventName: ""
  });
  const [feedbackLinkModal, setFeedbackLinkModal] = useState<{ open: boolean; url: string; eventName: string }>({
    open: false,
    url: "",
    eventName: ""
  });
  const [feedbackQrCodeModal, setFeedbackQrCodeModal] = useState<{ open: boolean; qrCodeDataUrl: string; eventName: string }>({
    open: false,
    qrCodeDataUrl: "",
    eventName: ""
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isChrome, isMobileSafari } = useBrowserDetection();
  const [location] = useLocation();
  
  // Check if we're on a tracker page for this specific event
  const isOnTrackerPage = location === `/event/${jamNight.id}` || location === `/event/${jamNight.id}/participants`;


  const toggleCompletionMutation = useMutation({
    mutationFn: async (jamNightId: number) => {
      return apiRequest("PATCH", `/api/events/${jamNightId}/toggle-completed`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      toast({
        title: "Success",
        description: "Event completion status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update event completion status",
        variant: "destructive",
      });
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (jamNightId: number) => {
      return apiRequest("DELETE", `/api/events/${jamNightId}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Success",
        description: "Event deleted successfully",
      });
      // Use setTimeout to prevent focus-related navigation issues
      setTimeout(() => {
        setDeleteDialogOpen(false);
      }, 0);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete event",
        variant: "destructive",
      });
    },
  });


  const generateSignupLinkMutation = useMutation({
    mutationFn: async ({ jamNightId, eventName }: { jamNightId: number; eventName: string }): Promise<{ signupUrl: string; eventName: string }> => {
      const response = await apiRequest("POST", `/api/events/${jamNightId}/signup-link`, {});
      const data = await response.json();
      return { ...data, eventName };
    },
    onSuccess: (data: { signupUrl: string; eventName: string }) => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      const fullUrl = `${window.location.origin}${data.signupUrl}`;
      
      setSignupLinkModal({
        open: true,
        url: fullUrl,
        eventName: data.eventName
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error", 
        description: error.message || "Failed to generate signup link",
        variant: "destructive",
      });
    },
  });

  const toggleSignupLinkMutation = useMutation({
    mutationFn: async ({ jamNightId, enabled }: { jamNightId: number; enabled: boolean }): Promise<{ signupEnabled: boolean }> => {
      const response = await apiRequest("PATCH", `/api/events/${jamNightId}/signup-link`, { enabled });
      return response.json();
    },
    onSuccess: (data: { signupEnabled: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      toast({
        title: "Success",
        description: `Signup link ${data.signupEnabled ? 'enabled' : 'disabled'} successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to toggle signup link",
        variant: "destructive",
      });
    },
  });

  const updateSignupLimitMutation = useMutation({
    mutationFn: async ({ jamNightId, limit }: { jamNightId: number; limit: number | null }): Promise<{ signupLimit: number | null; signupEnabled: boolean }> => {
      const response = await apiRequest("PATCH", `/api/events/${jamNightId}/signup-limit`, { limit });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      toast({
        title: "Success",
        description: data.signupEnabled 
          ? "Signup limit updated and signups re-enabled"
          : "Signup limit updated successfully",
      });
      // Use setTimeout to prevent focus-related navigation issues
      setTimeout(() => {
        setSignupLimitModal(prev => ({ ...prev, open: false }));
      }, 0);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update signup limit",
        variant: "destructive",
      });
    },
  });

  const toggleRaffleMutation = useMutation({
    mutationFn: async ({ jamNightId, enabled }: { jamNightId: number; enabled: boolean }): Promise<{ raffleEnabled: boolean }> => {
      const response = await apiRequest("PATCH", `/api/events/${jamNightId}/raffle-toggle`, { enabled });
      return response.json();
    },
    onSuccess: (data: { raffleEnabled: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      toast({
        title: "Success",
        description: `Raffles ${data.raffleEnabled ? 'enabled' : 'disabled'} for this event`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to toggle raffle setting",
        variant: "destructive",
      });
    },
  });

  const generateFeedbackLinkMutation = useMutation({
    mutationFn: async ({ jamNightId, eventName }: { jamNightId: number; eventName: string }): Promise<{ feedbackUrl: string; eventName: string }> => {
      const response = await apiRequest("POST", `/api/feedback/generate-url/${jamNightId}`, {});
      const data = await response.json();
      return { ...data, eventName };
    },
    onSuccess: (data: { feedbackUrl: string; eventName: string }) => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      
      setFeedbackLinkModal({
        open: true,
        url: data.feedbackUrl,
        eventName: data.eventName
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error", 
        description: error.message || "Failed to generate feedback link",
        variant: "destructive",
      });
    },
  });

  const toggleFeedbackLinkMutation = useMutation({
    mutationFn: async ({ jamNightId, enabled }: { jamNightId: number; enabled: boolean }): Promise<{ feedbackEnabled: boolean }> => {
      const response = await apiRequest("PUT", `/api/feedback/toggle/${jamNightId}`, { enabled });
      return response.json();
    },
    onSuccess: (data: { feedbackEnabled: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["jamNights"] });
      // Only invalidate specific event query if we're on a tracker page
      if (isOnTrackerPage) {
        queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      }
      toast({
        title: "Success",
        description: `Feedback ${data.feedbackEnabled ? 'enabled' : 'disabled'} successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to toggle feedback",
        variant: "destructive",
      });
    },
  });

  const handleSignupLimit = () => {
    setSignupLimitModal({
      open: true,
      jamNightId: jamNight.id,
      currentLimit: jamNight.signupLimit,
      eventName: jamNight.name
    });
    setSignupLimitValue(jamNight.signupLimit?.toString() || "");
  };

  const handleSubmitSignupLimit = () => {
    const limit = signupLimitValue.trim() === "" ? null : parseInt(signupLimitValue);
    updateSignupLimitMutation.mutate({ jamNightId: jamNight.id, limit });
  };

  const handleShowQRCode = async () => {
    if (!hasValidSignupUrl) {
      toast({
        title: "No signup link",
        description: "Please generate a signup link first.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const fullUrl = `${window.location.origin}/signup/${jamNight.signupUrl}`;
      const qrCodeDataUrl = await QRCode.toDataURL(fullUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodeModal({
        open: true,
        qrCodeDataUrl,
        eventName: jamNight.name
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate QR code",
        variant: "destructive",
      });
    }
  };

  const handleShowFeedbackQRCode = async () => {
    if (!hasValidFeedbackUrl) {
      toast({
        title: "No feedback link",
        description: "Please generate a feedback link first.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const fullUrl = `${window.location.origin}/feedback/${jamNight.feedbackUrl}`;
      const qrCodeDataUrl = await QRCode.toDataURL(fullUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setFeedbackQrCodeModal({
        open: true,
        qrCodeDataUrl,
        eventName: jamNight.name
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate feedback QR code",
        variant: "destructive",
      });
    }
  };

  if (!canManageEvent) {
    return null;
  }

  const hasValidSignupUrl = jamNight.signupUrl && jamNight.signupUrl.trim() !== "";
  const hasValidFeedbackUrl = jamNight.feedbackUrl && jamNight.feedbackUrl.trim() !== "";

  return (
    <div 
      onClick={(e) => {
        // Prevent any clicks within the dropdown area from bubbling to the card
        e.stopPropagation();
      }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "icon" ? (
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 w-8 p-0 hover:bg-white/20 rounded-full ${className}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              className={`bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-lg transition-all duration-300 ${className}`}
              style={{
                boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreVertical className="h-4 w-4 mr-2" />
              Manage Event
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className={`${
            (isChrome || isMobileSafari) 
              ? 'bg-white border-gray-200 shadow-xl' 
              : 'bg-white/20 backdrop-blur-xl border-white/30 shadow-2xl'
          } transition-all duration-300`}
          style={{
            backgroundColor: (isChrome || isMobileSafari) ? 'white' : undefined,
            backdropFilter: (isChrome || isMobileSafari) ? 'none' : undefined,
            boxShadow: (isChrome || isMobileSafari) 
              ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              : '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
          }}
        >
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setEditEventModalOpen(true);
            }}
            className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
          >
            <Edit3 className="h-4 w-4" />
            Edit Event
          </DropdownMenuItem>
          
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setLogoModalOpen(true);
            }}
            className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
          >
            <Image className="h-4 w-4" />
            Manage Logo
          </DropdownMenuItem>
          
          {!hasValidSignupUrl && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                generateSignupLinkMutation.mutate({ jamNightId: jamNight.id, eventName: jamNight.name });
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <Link className="h-4 w-4" />
              Get Signup Link
            </DropdownMenuItem>
          )}
          
          {hasValidSignupUrl && jamNight.signupEnabled && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                const fullUrl = `${window.location.origin}/signup/${jamNight.signupUrl}`;
                navigator.clipboard.writeText(fullUrl);
                toast({
                  title: "Signup link copied!",
                  description: "The signup link has been copied to your clipboard.",
                });
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <Link className="h-4 w-4" />
              Copy Signup Link
            </DropdownMenuItem>
          )}
          
          {hasValidSignupUrl && jamNight.signupEnabled && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleShowQRCode();
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <QrCode className="h-4 w-4" />
              Signup QR Code
            </DropdownMenuItem>
          )}
          
          {hasValidSignupUrl && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                toggleSignupLinkMutation.mutate({ 
                  jamNightId: jamNight.id, 
                  enabled: !jamNight.signupEnabled 
                });
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              {jamNight.signupEnabled ? (
                <>
                  <Unlock className="h-4 w-4" />
                  Disable Signup
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  Enable Signup
                </>
              )}
            </DropdownMenuItem>
          )}
          
          {jamNight.signupEnabled && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleSignupLimit();
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <Users className="h-4 w-4" />
              Manage Signup Limit
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              toggleCompletionMutation.mutate(jamNight.id);
            }}
            className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
          >
            {jamNight.completed ? (
              <>
                <Unlock className="h-4 w-4" />
                Mark as In Progress
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Mark as Completed
              </>
            )}
          </DropdownMenuItem>
          
          {!hasValidFeedbackUrl && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                generateFeedbackLinkMutation.mutate({ jamNightId: jamNight.id, eventName: jamNight.name });
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <MessageSquare className="h-4 w-4" />
              Get Feedback Link
            </DropdownMenuItem>
          )}
          
          {hasValidFeedbackUrl && jamNight.feedbackEnabled && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                const fullUrl = `${window.location.origin}/feedback/${jamNight.feedbackUrl}`;
                navigator.clipboard.writeText(fullUrl);
                toast({
                  title: "Feedback link copied!",
                  description: "The feedback link has been copied to your clipboard.",
                });
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <MessageSquare className="h-4 w-4" />
              Copy Feedback Link
            </DropdownMenuItem>
          )}
          
          {hasValidFeedbackUrl && jamNight.feedbackEnabled && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleShowFeedbackQRCode();
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <QrCode className="h-4 w-4" />
              Feedback QR Code
            </DropdownMenuItem>
          )}
          
          {hasValidFeedbackUrl && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                toggleFeedbackLinkMutation.mutate({ 
                  jamNightId: jamNight.id, 
                  enabled: !jamNight.feedbackEnabled 
                });
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              {jamNight.feedbackEnabled ? (
                <>
                  <Unlock className="h-4 w-4" />
                  Disable Feedback
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  Enable Feedback
                </>
              )}
            </DropdownMenuItem>
          )}

          {hasValidFeedbackUrl && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/event-feedback/${jamNight.id}`, '_blank');
              }}
              className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
            >
              <BarChart className="h-4 w-4" />
              View Feedback Analytics
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              toggleRaffleMutation.mutate({ 
                jamNightId: jamNight.id, 
                enabled: !jamNight.raffleEnabled 
              });
            }}
            className="flex items-center gap-2 hover:bg-white/20 focus:bg-white/20 transition-colors duration-200"
          >
            <Ticket className="h-4 w-4" />
            {jamNight.raffleEnabled ? 'Disable Raffles' : 'Enable Raffles'}
          </DropdownMenuItem>
          
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(true);
            }}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50/20 focus:bg-red-50/20 transition-colors duration-200"
          >
            <Trash2 className="h-4 w-4" />
            Delete Event
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{jamNight.name}"? This action cannot be undone and will permanently remove all associated data including participants and messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEventMutation.mutate(jamNight.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={editEventModalOpen}
        onOpenChange={setEditEventModalOpen}
        jamNight={jamNight}
      />

      {/* Event Logo Modal */}
      <EventLogoModal
        isOpen={logoModalOpen}
        onOpenChange={setLogoModalOpen}
        eventId={jamNight.id}
        eventName={jamNight.name}
        currentLogoUrl={jamNight.logoUrl}
      />

      {/* Signup Link Display Modal */}
      <Dialog open={signupLinkModal.open} onOpenChange={(open) => setSignupLinkModal(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signup Link Generated</DialogTitle>
            <DialogDescription >
              Your signup link for "{signupLinkModal.eventName}" has been generated. Public signups are now enabled via the URL below.

              Additional signup management options (such as generating a QR code, setting limits or disabling / re-enabling signup) are now available in the manage event menu.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 -mt-6">
            <div className="flex gap-2 mt-2">
              <Input
                value={signupLinkModal.url}
                disabled
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(signupLinkModal.url);
                  toast({
                    title: "Copied!",
                    description: "Signup link copied to clipboard",
                  });
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signup Limit Modal */}
      <Dialog open={signupLimitModal.open} onOpenChange={(open) => setSignupLimitModal(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Signup Limit</DialogTitle>
            <DialogDescription>
              Set the maximum number of participants that can sign up for "{signupLimitModal.eventName}". Leave blank for unlimited signups.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="signup-limit">Maximum Participants</Label>
            <Input
              id="signup-limit"
              type="number"
              min="1"
              value={signupLimitValue}
              onChange={(e) => setSignupLimitValue(e.target.value)}
              placeholder="Enter limit (optional)"
              className="mt-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmitSignupLimit();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignupLimitModal(prev => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitSignupLimit}
              className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300 mb-2"

              disabled={updateSignupLimitMutation.isPending}
            >
              Update Limit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrCodeModal.open} onOpenChange={(open) => setQrCodeModal(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code for "{qrCodeModal.eventName}"</DialogTitle>
            <DialogDescription>
              Scan this QR code to access the signup page for this event.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6 space-y-4">
            {qrCodeModal.qrCodeDataUrl && (
              <img 
                src={qrCodeModal.qrCodeDataUrl} 
                alt="QR Code for event signup" 
                className="rounded-lg shadow-lg"
              />
            )}
            <p className="text-sm text-gray-600 text-center">
              Share this QR code for easy event signup access
            </p>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button
              onClick={() => {
                if (qrCodeModal.qrCodeDataUrl) {
                  // Create a download link for the QR code
                  const link = document.createElement('a');
                  link.download = `${qrCodeModal.eventName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_qr_code.png`;
                  link.href = qrCodeModal.qrCodeDataUrl;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  toast({
                    title: "QR Code Downloaded",
                    description: "The QR code has been saved to your downloads.",
                  });
                }
              }}
              className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              Download QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Link Display Modal */}
      <Dialog open={feedbackLinkModal.open} onOpenChange={(open) => setFeedbackLinkModal(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Feedback Link Generated</DialogTitle>
            <DialogDescription>
              Your feedback link for "{feedbackLinkModal.eventName}" has been generated. Participants can now submit feedback via the URL below.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 -mt-6">
            <div className="flex gap-2 mt-2">
              <Input
                value={feedbackLinkModal.url}
                disabled
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(feedbackLinkModal.url);
                  toast({
                    title: "Copied!",
                    description: "Feedback link copied to clipboard",
                  });
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback QR Code Modal */}
      <Dialog open={feedbackQrCodeModal.open} onOpenChange={(open) => setFeedbackQrCodeModal(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Feedback QR Code for "{feedbackQrCodeModal.eventName}"</DialogTitle>
            <DialogDescription>
              Scan this QR code to access the feedback form for this event.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6 space-y-4">
            {feedbackQrCodeModal.qrCodeDataUrl && (
              <img 
                src={feedbackQrCodeModal.qrCodeDataUrl} 
                alt="QR Code for event feedback" 
                className="rounded-lg shadow-lg"
              />
            )}
            <p className="text-sm text-gray-600 text-center">
              Share this QR code for easy feedback submission
            </p>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button
              onClick={() => {
                if (feedbackQrCodeModal.qrCodeDataUrl) {
                  // Create a download link for the QR code
                  const link = document.createElement('a');
                  link.download = `${feedbackQrCodeModal.eventName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_feedback_qr_code.png`;
                  link.href = feedbackQrCodeModal.qrCodeDataUrl;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  toast({
                    title: "QR Code Downloaded",
                    description: "The feedback QR code has been saved to your downloads.",
                  });
                }
              }}
              className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              Download QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}